import { BaseService } from '@/infrastructure/api/base.service'
import type { ICompanyDetailService } from '@/domain/interfaces/ICompanyDetailService'
import type {
  CompanyDetailResponse,
  CompanyLogoResponse,
  ContactCountResponse,
  SimilarSitesAIResponse,
  DatapointsResponse,
} from '@/domain/entities/CompanyDetail'

// Vue 2 parity for the Similar Sites (AI) tab: the AI candidate pool size and the (currently unused)
// turnover-rank window the backend expects. `take` stays user-driven (20/40/60/80/100).
const SIMILAR_SITES_MAX_RESULTS = 300
const SIMILAR_SITES_TURNOVER_RANK = 150

export class CompanyDetailService extends BaseService implements ICompanyDetailService {
  constructor() {
    super('')
  }

  async getCompanyDetail(
    id: string,
    savedSearchId?: string,
  ): Promise<CompanyDetailResponse> {
    return this.post<CompanyDetailResponse>(
      '/search/detail2content',
      { id, savedSearchId: savedSearchId || '' },
      { timeout: 120_000 },
    )
  }

  async getCompanyLogo(host: string): Promise<string> {
    const response = await this.get<CompanyLogoResponse>(
      `/V2/Image/GetSingleCompanyLogo?url=${encodeURIComponent(host)}`,
    )
    return response.logo || ''
  }

  async getContactCount(
    leadId: string,
    savedSearchId?: string,
  ): Promise<ContactCountResponse> {
    return this.post<ContactCountResponse>(
      '/V2/SearchBase/GetContactCountFromCompany',
      {
        LeadId: leadId,
        SavedSearchId: savedSearchId || '',
      },
    )
  }

  async getSimilarSitesAI(
    leadEncId: string,
    take: number = 20,
  ): Promise<SimilarSitesAIResponse> {
    return this.post<SimilarSitesAIResponse>('/V2/LookAlike/GetSimilarSitesAI', {
      leadEncId,
      shouldUseBoIndex: false,
      // Vue 2 parity (CompanyDetail.vue getSimilarSitesAI): request a large candidate pool from the
      // SimilarSites AI API, then the backend filters to leads we actually have and returns `take`.
      // Sending maxResults = take shrank the pool, so V3 showed a different, smaller set of similar
      // sites than V2. turnoverRank mirrors V2 for parity (the backend currently ignores it).
      maxResults: SIMILAR_SITES_MAX_RESULTS,
      turnoverRank: SIMILAR_SITES_TURNOVER_RANK,
      take,
    })
  }

  async getDatapoints(): Promise<DatapointsResponse> {
    return this.post<DatapointsResponse>('/V2/Datapoint/GetDatapoints', {})
  }

  async logCopiedData(copiedValue: string, copiedType: string): Promise<void> {
    await this.post('/search/AddOrUpdateCopiedContactData', {
      copiedValue,
      copiedType,
    })
  }
}
